// BUG-M005 FIX: Enhanced environment validation
const requiredEnvVars = {
  VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
  VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
} as const;

const optionalEnvVars = {
  VITE_OPENAI_API_KEY: import.meta.env.VITE_OPENAI_API_KEY,
  VITE_RESEND_API_KEY: import.meta.env.VITE_RESEND_API_KEY,
  VITE_STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  VITE_APP_URL: import.meta.env.VITE_APP_URL,
} as const;

export interface EnvironmentStatus {
  valid: boolean;
  missing: string[];
  warnings: string[];
  errors: string[];
}

export function validateEnvironment(): EnvironmentStatus {
  const missing: string[] = [];
  const warnings: string[] = [];
  const errors: string[] = [];

  // Check required variables
  Object.entries(requiredEnvVars).forEach(([key, value]) => {
    if (!value) {
      missing.push(key);
    }
  });

  if (missing.length > 0) {
    errors.push(`Missing required environment variables: ${missing.join(', ')}`);
  }

  // Validate format of required variables
  if (requiredEnvVars.VITE_SUPABASE_URL) {
    if (!requiredEnvVars.VITE_SUPABASE_URL.startsWith('https://')) {
      errors.push('VITE_SUPABASE_URL must start with https://');
    }
    if (!requiredEnvVars.VITE_SUPABASE_URL.includes('supabase.co')) {
      warnings.push('VITE_SUPABASE_URL may be incorrect (should contain supabase.co)');
    }
  }

  if (requiredEnvVars.VITE_SUPABASE_ANON_KEY) {
    if (requiredEnvVars.VITE_SUPABASE_ANON_KEY.length < 100) {
      errors.push('VITE_SUPABASE_ANON_KEY appears to be invalid (too short)');
    }
  }

  // Check optional variables
  Object.entries(optionalEnvVars).forEach(([key, value]) => {
    if (!value) {
      warnings.push(key);
    }
  });

  // Validate Stripe configuration if present
  if (optionalEnvVars.VITE_STRIPE_PUBLISHABLE_KEY) {
    if (!optionalEnvVars.VITE_STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
      errors.push('VITE_STRIPE_PUBLISHABLE_KEY should start with pk_');
    }

    // Check for required Stripe price IDs
    const stripeEnvVars = [
      'STRIPE_PRICE_PROFESSIONAL_MONTHLY',
      'STRIPE_PRICE_PROFESSIONAL_ANNUAL',
      'STRIPE_PRICE_TEAM_MONTHLY',
      'STRIPE_PRICE_TEAM_ANNUAL',
      'STRIPE_PRICE_ENTERPRISE_MONTHLY',
      'STRIPE_PRICE_ENTERPRISE_ANNUAL',
    ];

    const missingPriceIds = stripeEnvVars.filter(key => !import.meta.env[key]);
    if (missingPriceIds.length > 0) {
      warnings.push(`Stripe configured but missing price IDs: ${missingPriceIds.join(', ')}`);
    }
  }

  // Log warnings in development
  if (import.meta.env.MODE === 'development') {
    if (errors.length > 0) {
      console.error('❌ Environment configuration errors:', errors);
    }
    if (warnings.length > 0) {
      console.warn(`⚠️  Environment configuration warnings:
${warnings.map(w => `  - ${w}`).join('\n')}

The application will work with limited functionality:
  - Without VITE_OPENAI_API_KEY: AI features will not work
  - Without VITE_RESEND_API_KEY: Email sending will not work
  - Without VITE_STRIPE_PUBLISHABLE_KEY: Paid plans will not be available
  - Without VITE_APP_URL: Some links may not work correctly
      `);
    }
  }

  // Throw error if critical issues found
  if (missing.length > 0) {
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }

  if (errors.length > 0 && import.meta.env.MODE === 'production') {
    throw new Error(`Environment configuration errors: ${errors.join('; ')}`);
  }

  return {
    valid: errors.length === 0,
    missing,
    warnings,
    errors
  };
}

export const env = {
  supabaseUrl: requiredEnvVars.VITE_SUPABASE_URL,
  supabaseAnonKey: requiredEnvVars.VITE_SUPABASE_ANON_KEY,
  openaiApiKey: optionalEnvVars.VITE_OPENAI_API_KEY,
  resendApiKey: optionalEnvVars.VITE_RESEND_API_KEY,
  stripePublishableKey: optionalEnvVars.VITE_STRIPE_PUBLISHABLE_KEY,
  mode: import.meta.env.MODE,
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
};
